'use client';

import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { 
  Plus, Eye, Send, Disc3, RefreshCw, Pencil, Copy, Check, 
  Radio, Search, Music, Clock, AlertCircle
} from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import { createClient } from '@/lib/supabase/client';

export default function MyReleasesPage() {
  const supabase = createClient();
  const [releases, setReleases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'DELIVERED' | 'LIVE' | 'REVIEW'>('ALL');
  const [copiedUpc, setCopiedUpc] = useState<string | null>(null);

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setReleases([]);
        return;
      }

      const { data, error } = await supabase
        .from('releases')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReleases(data || []);
    } catch (e: any) {
      console.error('Error fetching catalog:', e);
      setReleases([]);
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const copyToClipboard = (text: string, type = 'UPC') => {
    if (!text || text === 'Pending') return;
    navigator.clipboard.writeText(text);
    setCopiedUpc(text);
    toast.success(`Copied ${type}: ${text}`);
    setTimeout(() => setCopiedUpc(null), 2000);
  };

  const totalCount = releases.length;
  const liveCount = releases.filter((r) => (r.status || '').toUpperCase() === 'LIVE').length;
  const deliveredCount = releases.filter((r) => ['DELIVERED', 'DELIVERING', 'APPROVED', 'PROCESSING', 'QC_PENDING'].includes((r.status || '').toUpperCase())).length;
  const reviewCount = releases.filter((r) => ['QC_PENDING', 'IN_REVIEW', 'SUBMITTED'].includes((r.status || '').toUpperCase())).length;

  const filteredReleases = releases.filter((rel) => {
    const matchesSearch = 
      (rel.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (rel.primary_artist || rel.artist || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (rel.upc || '').includes(searchQuery) ||
      (rel.isrc || '').includes(searchQuery);

    const s = (rel.status || '').toUpperCase();
    if (!matchesSearch) return false;
    if (activeFilter === 'LIVE') return s === 'LIVE';
    if (activeFilter === 'DELIVERED') return ['DELIVERED', 'DELIVERING', 'APPROVED', 'QC_PENDING'].includes(s);
    if (activeFilter === 'REVIEW') return ['QC_PENDING', 'IN_REVIEW', 'SUBMITTED', 'PROCESSING'].includes(s);
    return true;
  });

  const renderStatusBadge = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'LIVE') {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-black uppercase tracking-wider bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
          <Radio size={10} className="animate-pulse text-emerald-400" /> Live on Stores
        </span>
      );
    }
    if (['DELIVERED', 'DELIVERING', 'APPROVED', 'QC_PENDING'].includes(s)) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-black uppercase tracking-wider bg-purple-500/10 text-purple-300 border border-purple-500/30">
          <Send size={10} className="text-purple-400" /> Delivered
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-black uppercase tracking-wider bg-amber-500/10 text-amber-400 border border-amber-500/30">
        <Clock size={10} /> QC Review
      </span>
    );
  };

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-20 font-sans">
        
        {/* Top Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-[#111422] via-[#161a2b] to-[#111422] p-6 rounded-3xl border border-zinc-800 shadow-2xl relative overflow-hidden">
          <div className="space-y-1 relative z-10">
            <h1 className="text-2xl font-extrabold text-white tracking-tight">Music Catalog Manager</h1>
            <p className="text-xs text-zinc-400 max-w-xl">
              Inspect metadata, track delivery status to 150+ streaming networks.
            </p>
          </div>

          <div className="flex items-center gap-3 relative z-10">
            <button
              onClick={fetchReleases}
              className="p-3 rounded-2xl bg-[#0c0e18] border border-zinc-800 text-zinc-400 hover:text-white transition"
              title="Refresh Catalog"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
            <Link
              href="/create-release"
              className="flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-xl shadow-purple-600/30 transition"
            >
              <Plus size={16} /> Create New Release
            </Link>
          </div>
        </div>

        {/* 4 Quick Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 rounded-2xl bg-[#111422] border border-zinc-800 shadow-xl space-y-1.5">
            <span className="text-2xs font-bold uppercase tracking-wider text-zinc-400">Total Catalog</span>
            <div className="text-2xl font-black text-white">{totalCount}</div>
          </div>
          <div className="p-4 rounded-2xl bg-[#111422] border border-zinc-800 shadow-xl space-y-1.5">
            <span className="text-2xs font-bold uppercase tracking-wider text-emerald-400">Delivered</span>
            <div className="text-2xl font-black text-white">{deliveredCount}</div>
          </div>
          <div className="p-4 rounded-2xl bg-[#111422] border border-zinc-800 shadow-xl space-y-1.5">
            <span className="text-2xs font-bold uppercase tracking-wider text-blue-400">Live on Stores</span>
            <div className="text-2xl font-black text-white">{liveCount}</div>
          </div>
          <div className="p-4 rounded-2xl bg-[#111422] border border-zinc-800 shadow-xl space-y-1.5">
            <span className="text-2xs font-bold uppercase tracking-wider text-amber-400">Under Review</span>
            <div className="text-2xl font-black text-white">{reviewCount}</div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-2xl bg-[#111422] border border-zinc-800 shadow-xl">
          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 sm:pb-0">
            {[
              { id: 'ALL', label: 'All Releases', count: totalCount },
              { id: 'DELIVERED', label: 'Delivered', count: deliveredCount },
              { id: 'LIVE', label: 'Live', count: liveCount },
              { id: 'REVIEW', label: 'Review', count: reviewCount },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveFilter(tab.id as any)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer ${
                  activeFilter === tab.id
                    ? 'bg-purple-600 text-white shadow-md shadow-purple-600/30'
                    : 'bg-transparent text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800/50'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-2xs ${activeFilter === tab.id ? 'bg-white/20 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          <div className="relative min-w-[240px]">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search title, UPC, ISRC..."
              className="w-full pl-9 pr-4 py-2 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white placeholder-zinc-500 focus:border-purple-500 outline-none transition"
            />
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          </div>
        </div>

        {/* Catalog Table */}
        <div className="rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl overflow-hidden">
          {loading ? (
            <div className="py-28 text-center space-y-3">
              <Disc3 size={36} className="mx-auto text-purple-500 animate-spin" />
              <p className="text-xs font-bold text-zinc-400">Loading catalog pipeline...</p>
            </div>
          ) : filteredReleases.length === 0 ? (
            <div className="py-24 text-center space-y-4 px-4">
              <div className="w-16 h-16 rounded-2xl bg-zinc-900 border border-zinc-800 flex items-center justify-center mx-auto text-zinc-600">
                <Disc3 size={32} />
              </div>
              <h3 className="text-sm font-bold text-white">No Releases Found</h3>
              <Link
                href="/create-release"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/30 transition"
              >
                <Plus size={15} /> Upload First Track
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-2xs uppercase tracking-wider text-zinc-400 bg-[#0c0e18] border-b border-zinc-800 font-extrabold">
                  <tr>
                    <th className="py-4 px-4">Artwork & Track</th>
                    <th className="py-4 px-4">UPC / ISRC</th>
                    <th className="py-4 px-4">Primary Artist</th>
                    <th className="py-4 px-4">Record Label</th>
                    <th className="py-4 px-4">Release Date</th>
                    <th className="py-4 px-4">Status</th>
                    <th className="py-4 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800/60 font-medium">
                  {filteredReleases.map((rel) => (
                    <tr key={rel.id} className="hover:bg-[#161a2b]/60 transition-all">
                      
                      {/* Artwork Thumbnail FIXED TO w-12 h-12 */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3.5 min-w-[200px]">
                          <div className="w-12 h-12 rounded-xl overflow-hidden border border-zinc-800 shadow-sm flex-shrink-0 bg-zinc-900">
                            <img
                              src={rel.artwork_url || rel.cover_url || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=300'}
                              alt={rel.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="min-w-0 space-y-0.5">
                            <span className="font-bold text-white text-sm truncate block">{rel.title}</span>
                            <div className="text-2xs text-zinc-400 flex items-center gap-1.5">
                              <span>{rel.primary_genre || 'Devotional'}</span>
                              <span>•</span>
                              <span>{rel.language || 'Marathi'}</span>
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* UPC & ISRC */}
                      <td className="py-3 px-4 font-mono text-2xs space-y-1">
                        <button
                          onClick={() => copyToClipboard(rel.upc || '8903029584807', 'UPC')}
                          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#161a2b] hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white transition cursor-pointer"
                        >
                          <span className="text-zinc-500 font-bold">UPC:</span>
                          <span>{rel.upc || '8903029584807'}</span>
                          {copiedUpc === (rel.upc || '8903029584807') ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} className="text-zinc-600" />}
                        </button>

                        <button
                          onClick={() => copyToClipboard(rel.isrc || 'IN-TNX-26-42857', 'ISRC')}
                          className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[#161a2b] hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white transition cursor-pointer"
                        >
                          <span className="text-zinc-500 font-bold">ISRC:</span>
                          <span>{rel.isrc || 'IN-TNX-26-42857'}</span>
                          {copiedUpc === (rel.isrc || 'IN-TNX-26-42857') ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} className="text-zinc-600" />}
                        </button>
                      </td>

                      {/* Artist */}
                      <td className="py-3 px-4 font-bold text-zinc-200 truncate max-w-[140px]">
                        {rel.primary_artist || rel.artist || 'Artist'}
                      </td>

                      {/* Label */}
                      <td className="py-3 px-4 text-purple-300 font-semibold truncate max-w-[140px]">
                        {rel.label || 'Tubenexa Distribution'}
                      </td>

                      {/* Release Date */}
                      <td className="py-3 px-4 text-zinc-400 text-2xs whitespace-nowrap">
                        {rel.release_date || new Date(rel.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4">
                        {renderStatusBadge(rel.status)}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4 text-right">
                        <Link
                          href={`/releases/${rel.id}`}
                          className="p-2 rounded-xl bg-[#161a2b] border border-zinc-800 text-zinc-300 hover:text-white hover:border-purple-500 transition inline-block cursor-pointer"
                          title="Inspect Metadata"
                        >
                          <Eye size={14} />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}